import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  CheckCircle, WarningCircle, Play, ArrowsClockwise, Clock, Flask, Eye, Briefcase, Coins,
} from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
}
const STATUS_STYLE = { finished: "text-accent border-accent/30", running: "text-amber-400 border-amber-500/30", error: "text-destructive border-destructive/30" };

export default function BrightDataJobsCollection() {
  const [cfg, setCfg] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [hoursStr, setHoursStr] = useState("");
  const [maxQ, setMaxQ] = useState("");
  const [limitPer, setLimitPer] = useState("");
  const [tq, setTq] = useState("DevOps");
  const [tloc, setTloc] = useState("Lille");
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([
        api.get("/admin/collection/brightdata/config"),
        api.get("/admin/collection/brightdata/runs"),
      ]);
      setCfg(c.data); setRuns(r.data);
      setHoursStr((c.data.hours || []).join(", "));
      setMaxQ(String(c.data.max_queries_per_run ?? 15));
      setLimitPer(String(c.data.limit_per_query ?? 50));
    } catch { toast.error("Chargement Bright Data Emplois impossible"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCfg = async (patch) => {
    try {
      const { data } = await api.post("/admin/collection/brightdata/config", patch);
      setCfg(data);
      setHoursStr((data.hours || []).join(", "));
      setMaxQ(String(data.max_queries_per_run ?? 15));
      setLimitPer(String(data.limit_per_query ?? 50));
      toast.success("Configuration mise à jour");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const collectNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/collection/brightdata/collect", { max_queries: Number(maxQ) || undefined, it_france: true });
      if (data.error) toast.error(data.error);
      else toast.success(`Collecte IT · France lancée · ${data.queries_planned} requête(s)${data.simulate ? " · mode simulé (0 crédit)" : ""}`);
      setTimeout(load, 5000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const runTest = async () => {
    setTesting(true); setTestRes(null);
    try {
      const { data } = await api.post("/admin/collection/brightdata/test", { keyword: tq, location: tloc, country: "FR", limit: 8 });
      if (data.error) toast.error(data.error); else setTestRes(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setTesting(false); }
  };

  if (!cfg) return <div className="text-muted font-mono text-sm animate-pulse">Chargement Bright Data Emplois…</div>;
  const lastErr = cfg.last_run?.status === "error" ? cfg.last_run?.error : null;
  const noDataset = !cfg.jobs_dataset_id && !cfg.simulate;

  return (
    <div className="surface rounded-lg p-6 mb-6" data-testid="brightdata-jobs-panel">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-accent/12 border border-accent/25 flex items-center justify-center">
            <Briefcase size={20} weight="duotone" className="text-accent" />
          </div>
          <div>
            <h3 className="font-display font-bold text-fg flex items-center gap-2">
              Bright Data — Emplois
              <span className="text-[10px] font-mono text-muted border border-border rounded-full px-2 py-0.5">dataset auto-découvert</span>
              {cfg.simulate && <span className="text-[10px] font-mono text-amber-400 border border-amber-500/30 rounded-full px-2 py-0.5">simulé (0 crédit)</span>}
            </h3>
            <p className="text-xs text-muted">Découvre des offres (LinkedIn/Indeed…) via le dataset Emplois et les injecte dans les signaux (dédupliqués).</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`flex items-center gap-1 text-xs font-mono border rounded-full px-2 py-1 ${cfg.configured ? "text-accent border-accent/30" : "text-amber-400 border-amber-500/30"}`} data-testid="brightdata-jobs-status">
            {cfg.configured ? <CheckCircle size={12} weight="fill" /> : <WarningCircle size={12} weight="fill" />} {cfg.configured ? "Configurée" : "Dataset manquant"}
          </span>
          <button onClick={() => saveCfg({ enabled: !cfg.enabled })} data-testid="brightdata-jobs-toggle"
            className={`flex items-center gap-1.5 h-8 px-3 rounded-md border text-xs font-medium ${cfg.enabled ? "bg-accent/12 border-accent/40 text-accent" : "bg-surface border-border text-muted hover:text-fg"}`}>
            <CheckCircle size={13} weight={cfg.enabled ? "fill" : "regular"} /> Auto {cfg.enabled ? "activé" : "désactivé"}
          </button>
        </div>
      </div>

      {noDataset && (
        <div className="mb-4 text-xs bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded-md px-3 py-2" data-testid="brightdata-jobs-nodataset">
          Sélectionnez un « Dataset Jobs » dans <b>Fournisseurs de données → Bright Data</b> pour activer la collecte réelle.
        </div>
      )}
      {lastErr && <div className="mb-4 text-xs bg-destructive/10 border border-destructive/30 text-destructive rounded-md px-3 py-2" data-testid="brightdata-jobs-error">{lastErr}</div>}

      <div className="grid sm:grid-cols-4 gap-3 border-t border-border pt-4 mb-2">
        <div>
          <label className="overline block mb-1.5 flex items-center gap-1"><Clock size={12} /> Heures (UTC)</label>
          <Input className="bg-surface-2 border-border text-xs h-9" value={hoursStr} onChange={(e) => setHoursStr(e.target.value)} onBlur={() => saveCfg({ hours: hoursStr.split(",").map((h) => Number(h.trim())).filter((n) => !isNaN(n)) })} placeholder="8" data-testid="brightdata-jobs-hours" />
        </div>
        <div>
          <label className="overline block mb-1.5 flex items-center gap-1"><Coins size={12} /> Max requêtes / collecte</label>
          <Input type="number" className="bg-surface-2 border-border text-xs h-9" value={maxQ} onChange={(e) => setMaxQ(e.target.value)} onBlur={() => saveCfg({ max_queries_per_run: Number(maxQ) })} data-testid="brightdata-jobs-maxq" />
        </div>
        <div>
          <label className="overline block mb-1.5 flex items-center gap-1"><Briefcase size={12} /> Offres / requête</label>
          <Input type="number" className="bg-surface-2 border-border text-xs h-9" value={limitPer} onChange={(e) => setLimitPer(e.target.value)} onBlur={() => saveCfg({ limit_per_query: Number(limitPer) })} data-testid="brightdata-jobs-limit" />
        </div>
        <div className="flex items-end">
          <button onClick={collectNow} disabled={busy || !cfg.configured} data-testid="brightdata-jobs-collect-now"
            className="w-full flex items-center justify-center gap-2 bg-accent text-black font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-50 h-9">
            <Play size={15} weight="fill" className={busy ? "animate-pulse" : ""} /> {busy ? "Lancement…" : "Collecter (IT · France)"}
          </button>
        </div>
      </div>
      <p className="text-[11px] text-muted mb-4">Collecte manuelle ciblée : <b className="text-fg/80">emplois IT</b> (vos mots-clés actifs) sur <b className="text-fg/80">la France entière</b>. {cfg.simulate ? "En mode simulé, la collecte est une prévisualisation SANS ingestion." : "Chaque collecte consomme du quota Bright Data."}</p>

      {/* Test mode */}
      <div className="border-t border-border pt-4 mb-4">
        <div className="flex items-center gap-2 mb-2"><Flask size={15} className="text-accent" /><h4 className="font-display font-bold text-fg text-sm">Mode test (aucune ingestion)</h4></div>
        <div className="flex flex-wrap gap-2 mb-3">
          <Input className="bg-surface-2 border-border text-xs h-9 w-40" value={tq} onChange={(e) => setTq(e.target.value)} placeholder="Mot-clé" data-testid="brightdata-jobs-test-kw" />
          <Input className="bg-surface-2 border-border text-xs h-9 w-40" value={tloc} onChange={(e) => setTloc(e.target.value)} placeholder="Localisation" data-testid="brightdata-jobs-test-loc" />
          <button onClick={runTest} disabled={testing || !cfg.configured} className="h-9 px-4 rounded-md bg-surface border border-border text-xs text-fg hover:border-accent flex items-center gap-1 disabled:opacity-50" data-testid="brightdata-jobs-test-run">
            <Eye size={14} /> {testing ? "Test…" : "Tester"}
          </button>
        </div>
        {testRes && (
          <div className="bg-surface-2 border border-border rounded-md p-3 text-xs" data-testid="brightdata-jobs-test-result">
            <div className="font-mono text-muted mb-2">{testRes.simulated ? "SIMULÉ · " : ""}{testRes.raw_count} brut · {testRes.normalized_count} normalisés · {testRes.staffing_excluded} intérim exclus</div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {(testRes.sample || []).map((n, i) => (
                <div key={i} className="flex items-center gap-2 flex-wrap">
                  <span className="text-fg font-medium">{n.company_name}</span>
                  <span className="text-muted">— {n.role_title}</span>
                  {n.city && <span className="text-accent font-mono">· {n.city}</span>}
                  {n.posted_at && <span className="text-muted font-mono">· {n.posted_at}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Runs */}
      <div className="border-t border-border pt-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-display font-bold text-fg text-sm">Dernières collectes</h4>
          <button onClick={load} className="text-xs text-muted hover:text-accent flex items-center gap-1"><ArrowsClockwise size={12} /> Rafraîchir</button>
        </div>
        <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="brightdata-jobs-runs">
          {runs.length === 0 && <span className="text-xs text-muted">Aucune collecte pour l'instant.</span>}
          {runs.map((r) => (
            <div key={r.id} className="text-xs bg-surface-2 border border-border rounded-md px-3 py-2 flex items-center gap-3 flex-wrap">
              <span className={`font-mono border rounded-full px-2 py-0.5 ${STATUS_STYLE[r.status] || "text-muted border-border"}`}>{r.status}</span>
              <span className="text-muted font-mono">{fmtDate(r.created_at)}</span>
              {r.simulate && <span className="text-amber-400 font-mono">simulé</span>}
              <span className="font-mono text-fg/80">{r.records_received || 0} offres reçues</span>
              {r.status === "finished" && !r.simulate && <span className="font-mono text-fg/70">{r.created || 0} nouveaux · {r.updated || 0} enrichis · {r.cross_source_merges || 0} cross-source</span>}
              {r.note && <span className="text-muted">{r.note}</span>}
              {r.error && <span className="text-destructive">{r.error}</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
